import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

export default function Dashboard() {
  const [user, setUser] = useState(null)
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('entries')
  const [phase2Open, setPhase2Open] = useState(false)
  const [bestRank, setBestRank] = useState(null) // eslint-disable-line no-unused-vars
  const [showGuide, setShowGuide] = useState(false)
  const [leaderboard, setLeaderboard] = useState([])
  const [selectedEntry, setSelectedEntry] = useState(null)

  useEffect(() => {
    async function load() {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) { window.location.href = '/login'; return }
      const { data: userData } = await supabase.from('users').select('*').eq('id', authUser.id).single()
      setUser(userData)
      const { data: entriesData } = await supabase.from('entries').select('*').eq('user_id', authUser.id).order('entry_number')
      setEntries(entriesData || [])
      const { data: phase2Setting } = await supabase.from('settings').select('value').eq('key', 'phase2_open').single()
      setPhase2Open(phase2Setting?.value === 'true')

      const { data: lbData } = await supabase.from('leaderboard').select('*')
      setLeaderboard(lbData || [])
      const entryIds = (entriesData || []).map(e => e.id)
      const bestRank = lbData && lbData.length > 0
        ? Math.min(...entryIds.map(id => lbData.findIndex(p => p.id === id) + 1).filter(r => r > 0))
        : null
      setBestRank(bestRank)

      setLoading(false)
    }
    load()
  }, [])

  async function handleNewEntry() {
    const { data: { user: authUser } } = await supabase.auth.getUser()
    const entryNumber = entries.length + 1
    const entryName = `${user.display_name || user.name} ${entryNumber}`
    const { data, error } = await supabase.from('entries').insert({ user_id: authUser.id, entry_name: entryName, entry_number: entryNumber }).select().single()
    if (error) { alert('Something went wrong. Please try again.'); return }
    window.location.href = `/entry/${data.id}`
  }

  async function handleDeleteEntry(entryId, entryName) {
    if (!window.confirm(`Are you sure you want to delete "${entryName}"? This cannot be undone.`)) return
    await supabase.from('match_predictions').delete().eq('entry_id', entryId)
    await supabase.from('bonus_predictions').delete().eq('entry_id', entryId)
    await supabase.from('group_predictions').delete().eq('entry_id', entryId)
    const { error } = await supabase.from('entries').delete().eq('id', entryId)
    if (error) { alert('Something went wrong. Please try again.'); return }
    setEntries(prev => prev.filter(e => e.id !== entryId))
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    window.location.href = '/'
  }

  if (loading) return (
    <div>
      <nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav>
      <div className="container"><p className="text-muted">Loading...</p></div>
    </div>
  )


  return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => setShowGuide(true)} style={{ fontSize: '12px', color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>How to play</button>
          <a href="/change-password" style={{ fontSize: '12px', color: 'var(--text-3)' }}>Change password</a>
          <button className="btn btn-secondary" onClick={handleLogout} style={{ padding: '7px 14px', fontSize: '13px' }}>Log out</button>
        </div>
      </nav>

      {showGuide && (
        <div onClick={() => setShowGuide(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, overflowY: 'auto', padding: '40px 16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '560px', margin: '0 auto', background: 'var(--surface)', borderRadius: 'var(--r-xl)', padding: '28px', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '26px', margin: 0 }}>How to play</h2>
              <button onClick={() => setShowGuide(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--text-3)', padding: '4px' }}>✕</button>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>1. Editing your entry</p>
              <p style={{ fontSize: '14px', color: 'var(--text-2)', lineHeight: '1.7', marginBottom: '10px' }}>If you have already created an entry, do not click "+ New Entry" again. Find your existing entry on the dashboard and click <strong>Edit</strong>.</p>
              <div style={{ padding: '10px 14px', background: 'var(--gold-bg)', border: '1px solid var(--gold-border)', borderRadius: 'var(--r)', fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.6' }}>
                If you accidentally created a duplicate blank entry, use the <strong>Delete</strong> button on it to remove it.
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>2. Bonus picks - don't skip these</p>
              <p style={{ fontSize: '14px', color: 'var(--text-2)', lineHeight: '1.7', marginBottom: '10px' }}>Inside your entry form there are two tabs at the top - "Group Stage" and <strong>Bonus Picks</strong>. Click the Bonus Picks tab to predict the Golden Boot, Golden Gloves and Player of the Tournament.</p>
              <div style={{ padding: '10px 14px', background: 'var(--gold-bg)', border: '1px solid var(--gold-border)', borderRadius: 'var(--r)', fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.6' }}>
                Each correct bonus answer is worth <strong>8 points</strong>. Spelling doesn't need to be perfect - the organiser reviews all answers manually.
              </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <p style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>3. Save your entry</p>
              <p style={{ fontSize: '14px', color: 'var(--text-2)', lineHeight: '1.7' }}>Once you have filled in all predictions, scroll to the bottom and click <strong>Save Predictions</strong>. You can come back and edit anytime before entries close on 11 June.</p>
            </div>

            <button className="btn btn-primary" onClick={() => setShowGuide(false)} style={{ width: '100%', marginTop: '24px', padding: '10px', fontSize: '14px' }}>Got it</button>
          </div>
        </div>
      )}

      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1 style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '28px' }}>{user?.name}</h1>
            <span className="badge-green" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span className="pulse-dot"></span>Phase 1 · Group Stage Open
            </span>
          </div>
        </div>

        <div className="tab-bar">
          {['entries', 'scoring'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`tab-btn ${activeTab === tab ? 'active' : ''}`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'entries' && (
          <>
            {entries.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
                <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚽</div>
                <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', marginBottom: '8px' }}>No entries yet</h3>
                <p style={{ color: 'var(--text-2)', marginBottom: '24px' }}>Click "New Entry" to start predicting the World Cup</p>
                <button className="btn btn-primary" onClick={handleNewEntry}>+ New Entry</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {entries.map(entry => (
                  <div key={entry.id} className={entry.paid ? 'card-gold' : 'card'} style={{ padding: '24px', marginBottom: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: '600', fontSize: '16px' }}>{entry.entry_name}</span>
                          {entry.paid
                            ? <span className="badge-green">Active</span>
                            : <span className="badge-red">Payment pending</span>
                          }
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>Created {new Date(entry.created_at).toLocaleDateString()}</div>
                      </div>
                      {entry.paid && (() => {
                        const rank = leaderboard.findIndex(p => p.id === entry.id) + 1
                        const suffix = (n) => { const s = ['th','st','nd','rd'], v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]) }
const ordinal = suffix(rank)
                        return (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div className="gold-text" style={{ fontFamily: 'var(--ff-d)', fontSize: '38px', fontWeight: '700', lineHeight: '1' }}>
                              {rank > 0 ? ordinal : '-'}
                            </div>
                            <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-3)' }}>position</div>
                          </div>
                        )
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <a href={`/predictions/${entry.id}`} className="btn btn-secondary" style={{ fontSize: '13px', padding: '7px 14px' }}>My Points</a>
                      <a href={`/entry/${entry.id}`} className="btn btn-secondary" style={{ fontSize: '13px', padding: '7px 14px' }}>
                        {entry.phase1_locked ? 'View' : 'Edit'}
                      </a>
                      {entry.phase1_locked && phase2Open && (
                        <a href={`/phase2/${entry.id}`} className="btn btn-primary" style={{ fontSize: '13px', padding: '7px 14px' }}>Phase 2</a>
                      )}
                      {entry.phase1_locked && !phase2Open && (
                        <a href={`/phase2/${entry.id}`} className="btn btn-secondary" style={{ fontSize: '13px', padding: '7px 14px' }}>KO Picks</a>
                      )}
                      {!entry.paid && (
                        <button onClick={() => handleDeleteEntry(entry.id, entry.entry_name)} className="btn btn-danger" style={{ fontSize: '13px', padding: '7px 14px' }}>Delete</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            </>
        )}

        {activeTab === 'entries' && leaderboard.length > 0 && (
          <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', margin: 0 }}>Leaderboard</h2>
              <button className="btn btn-secondary" style={{ fontSize: '13px', padding: '7px 14px' }} onClick={async () => {
                const { data } = await supabase.from('leaderboard').select('*')
                setLeaderboard(data || [])
              }}>Refresh</button>
            </div>
            <div className="card-flat">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '48px' }}>#</th>
                    <th>Entry</th>
                    <th style={{ textAlign: 'right' }}>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((p, i) => {
                    const isOwn = entries.some(e => e.id === p.id)
                    return (
                      <tr key={p.id} onClick={() => setSelectedEntry(p)} style={{ cursor: 'pointer', ...(isOwn ? { background: 'var(--gold-bg)', borderLeft: '3px solid var(--gold)' } : {}) }}>
                        <td>
                          <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '20px', color: i === 0 ? '#b45309' : i === 1 ? '#6b7280' : i === 2 ? '#92400e' : 'var(--text-3)' }}>
                            {i + 1}
                          </span>
                        </td>
                        <td style={{ fontWeight: isOwn ? '600' : i < 3 ? '600' : '400', color: isOwn ? 'var(--gold)' : 'var(--text)' }}>{p.entry_name}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '20px', color: i === 0 ? 'var(--gold)' : 'var(--text)' }}>
                            {p.total_points}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selectedEntry && (
          <PlayerPredictionsModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
        )}

        {activeTab === 'scoring' && (
          <div className="card">
            <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '22px', marginBottom: '20px' }}>How scoring works</h3>
            <p style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '20px', lineHeight: '1.7' }}>
              Points are awarded after each match is confirmed. Exact score points include the outcome points - they are not stacked separately.
            </p>
            {[
              { label: 'Group Stage', rows: [
                ['Exact score correct (includes outcome)', '5 pts'],
                ['Correct result only (win, draw or loss)', '2 pts'],
                ['Team finishes in correct group position', '3 pts'],
                ['Team qualifies but in wrong position', '1 pt'],
              ]},
              { label: 'Knockout Stage', rows: [
                ['Exact score correct after 90 mins (includes outcome)', '6 pts'],
                ['Correct match winner, wrong score', '4 pts'],
                ['Correct penalty shootout winner', '8 pts'],
                ['Both finalists correctly predicted', '10 pts'],
              ]},
              { label: 'Bonus Points', rows: [
                ['Golden Boot winner correctly predicted', '8 pts'],
                ['Golden Gloves winner correctly predicted', '8 pts'],
                ['Player of the Tournament correctly predicted', '8 pts'],
              ]},
            ].map(section => (
              <div key={section.label} style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '11px', fontWeight: '600', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                  {section.label}
                </p>
                {section.rows.map(([desc, pts]) => (
                  <div key={desc} className="flex-between" style={{ fontSize: '13px', padding: '9px 0', borderBottom: '1px solid var(--border-sub)' }}>
                    <span style={{ color: 'var(--text-2)' }}>{desc}</span>
                    <span style={{ fontWeight: '600', color: 'var(--text)', minWidth: '48px', textAlign: 'right' }}>{pts}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── PLAYER PREDICTIONS MODAL ───────────────────────────────────────────────

function PlayerPredictionsModal({ entry, onClose }) {
  const [predictions, setPredictions] = useState([])
  const [bonuses, setBonuses] = useState([])
  const [koMatches, setKoMatches] = useState([])
  const [bracket, setBracket] = useState({})
  const [koPredMap, setKoPredMap] = useState({})
  const [loading, setLoading] = useState(true)

  function getWinner(pred, match) {
    if (!pred || pred.home === '' || pred.away === '' || pred.home === undefined || pred.away === undefined) return null
    const h = parseInt(pred.home)
    const a = parseInt(pred.away)
    if (isNaN(h) || isNaN(a)) return null
    if (h > a) return match.home
    if (a > h) return match.away
    if (h === a && pred.penalties_winner) {
      if (pred.penalties_winner === 'home') return match.home
      if (pred.penalties_winner === 'away') return match.away
    }
    return null
  }

  function getLoser(pred, match) {
    const winner = getWinner(pred, match)
    if (!winner) return null
    if (winner.id === match.home?.id) return match.away
    return match.home
  }

  function buildBracket(matches, predictions) {
    const resolved = {}
    const r32 = matches.filter(m => m.phase === 'knockout_r32')
    r32.forEach(m => { resolved[m.match_number] = { ...m, home: m.home || null, away: m.away || null } })

    const r16pairs = [[74,77],[73,75],[76,78],[79,80],[83,84],[81,82],[86,88],[85,87]]
    matches.filter(m => m.phase === 'knockout_r16').forEach((m, i) => {
      const m1 = resolved[r16pairs[i][0]]
      const m2 = resolved[r16pairs[i][1]]
      resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
    })

    const qfpairs = [[89,90],[93,94],[91,92],[95,96]]
    matches.filter(m => m.phase === 'knockout_qf').forEach((m, i) => {
      const m1 = resolved[qfpairs[i][0]]
      const m2 = resolved[qfpairs[i][1]]
      resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
    })

    const sfpairs = [[97,98],[99,100]]
    matches.filter(m => m.phase === 'knockout_sf').forEach((m, i) => {
      const m1 = resolved[sfpairs[i][0]]
      const m2 = resolved[sfpairs[i][1]]
      resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
    })

    const finalMatch = matches.find(m => m.phase === 'knockout_final')
    if (finalMatch) {
      const sf1 = resolved[101]
      const sf2 = resolved[102]
      resolved[finalMatch.match_number] = { ...finalMatch, home: sf1 ? getWinner(predictions[sf1.id], sf1) : null, away: sf2 ? getWinner(predictions[sf2.id], sf2) : null }
    }

    const thirdMatch = matches.find(m => m.phase === 'knockout_3rd')
    if (thirdMatch) {
      const sf1 = resolved[101]
      const sf2 = resolved[102]
      resolved[thirdMatch.match_number] = { ...thirdMatch, home: sf1 ? getLoser(predictions[sf1.id], sf1) : null, away: sf2 ? getLoser(predictions[sf2.id], sf2) : null }
    }

    return resolved
  }

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('match_predictions')
        .select(`*, matches(match_number, group_code, phase, actual_home_goals, actual_away_goals, result_confirmed, home:home_team_id(name, flag_emoji), away:away_team_id(name, flag_emoji))`)
        .eq('entry_id', entry.id)
      const sorted = (data || []).sort((a, b) => (b.matches?.match_number || 0) - (a.matches?.match_number || 0))
      setPredictions(sorted)

      const { data: koMatchData } = await supabase
        .from('matches')
        .select('*, home:home_team_id(id,name,flag_emoji), away:away_team_id(id,name,flag_emoji)')
        .in('phase', ['knockout_r32','knockout_r16','knockout_qf','knockout_sf','knockout_3rd','knockout_final'])
        .order('match_number')
      setKoMatches(koMatchData || [])

      const predMap = {}
      data?.forEach(p => {
        predMap[p.match_id] = {
          home: p.predicted_home_goals,
          away: p.predicted_away_goals,
          penalties_winner: p.predicted_penalties_winner || null
        }
      })
      setKoPredMap(predMap)
      setBracket(buildBracket(koMatchData || [], predMap))

      const { data: bonusData } = await supabase
        .from('bonus_predictions')
        .select('*')
        .eq('entry_id', entry.id)
      setBonuses(bonusData || [])

      setLoading(false)
    }
    load()
  }, [entry.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const totalPoints = predictions.reduce((sum, p) => sum + (p.points_awarded || 0), 0)
  const categoryLabels = { golden_boot: 'Golden Boot', golden_gloves: 'Golden Gloves', player_tournament: 'Player of the Tournament' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, overflowY: 'auto', padding: '40px 16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', margin: '0 auto', background: 'var(--surface)', borderRadius: 'var(--r-xl)', padding: '28px', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--ff-d)', fontSize: '24px', margin: 0, marginBottom: '4px' }}>{entry.entry_name}</h2>
            <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>{entry.player_name}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontFamily: 'var(--ff-d)', fontSize: '28px', fontWeight: '700', color: 'var(--gold)' }}>{totalPoints} pts</span>
            <button className="btn btn-secondary" onClick={onClose} style={{ padding: '7px 14px', fontSize: '13px' }}>Close</button>
          </div>
        </div>

        {loading ? (
          <p className="text-muted">Loading predictions...</p>
        ) : (
          <>
            {predictions.length === 0 ? (
              <p className="text-muted">No predictions submitted yet.</p>
            ) : (
              <div className="card-flat">
                <table>
                  <thead>
                    <tr>
                      <th>Match</th>
                      <th style={{ textAlign: 'center' }}>Prediction</th>
                      <th style={{ textAlign: 'center' }}>Actual</th>
                      <th style={{ textAlign: 'center' }}>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictions.map(p => {
                      const isKO = p.matches?.phase !== 'group'
                      const koMatch = isKO ? koMatches.find(m => m.id === p.match_id) : null
                      const resolved = koMatch ? (bracket[koMatch.match_number] || {}) : {}
                      const homeTeam = isKO ? (resolved.home || p.matches?.home) : p.matches?.home
                      const awayTeam = isKO ? (resolved.away || p.matches?.away) : p.matches?.away
                      const pred = isKO ? (koPredMap[p.match_id] || {}) : {}
                      const isDraw = isKO && p.predicted_home_goals !== null && p.predicted_away_goals !== null && p.predicted_home_goals === p.predicted_away_goals
                      const penWinner = isDraw ? (pred.penalties_winner === 'home' ? homeTeam : pred.penalties_winner === 'away' ? awayTeam : null) : null
                      const phaseLabel = p.matches?.phase === 'group' ? `Grp ${p.matches?.group_code}` : p.matches?.phase?.replace('knockout_', '').replace('r32', 'R32').replace('r16', 'R16').replace('qf', 'QF').replace('sf', 'SF').replace('3rd', '3rd').replace('final', 'Final').toUpperCase()
                      return (
                        <tr key={p.id}>
                          <td style={{ fontSize: '13px' }}>
                            <span style={{ fontSize: '10px', color: 'var(--text-3)', marginRight: '6px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.06em' }}>{phaseLabel}</span>
                            <div>{homeTeam?.flag_emoji} {homeTeam?.name || '?'} vs {awayTeam?.name || '?'} {awayTeam?.flag_emoji}</div>
                            {penWinner && <div style={{ fontSize: '11px', color: 'var(--gold)', marginTop: '3px' }}>Pen: {penWinner.flag_emoji} {penWinner.name}</div>}
                          </td>
                          <td style={{ textAlign: 'center', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                            {p.predicted_home_goals} - {p.predicted_away_goals}
                          </td>
                          <td style={{ textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '16px' }}>
                            {p.matches?.result_confirmed ? `${p.matches.actual_home_goals} - ${p.matches.actual_away_goals}` : '-'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {p.matches?.result_confirmed ? (
                              <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '18px', color: p.points_awarded > 0 ? 'var(--gold)' : 'var(--text-3)' }}>
                                {p.points_awarded > 0 ? `+${p.points_awarded}` : '0'}
                              </span>
                            ) : <span style={{ color: 'var(--text-3)' }}>-</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {bonuses.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '12px' }}>Bonus Predictions</h3>
                <div className="card-flat">
                  <table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Pick</th>
                        <th style={{ textAlign: 'center' }}>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bonuses.map(b => (
                        <tr key={b.id}>
                          <td style={{ fontSize: '13px', color: 'var(--text-2)' }}>{categoryLabels[b.category] || b.category}</td>
                          <td style={{ fontSize: '13px', fontWeight: '500' }}>{b.predicted_player_name}</td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '18px', color: b.points_awarded > 0 ? 'var(--gold)' : 'var(--text-3)' }}>
                              {b.points_awarded > 0 ? `+${b.points_awarded}` : '0'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}